// Employee Score → View tab, as Excel sheets.
//
// The Download button writes the table on screen, so these builders take the
// rows the page is rendering — already role-scoped, filtered, searched and
// sorted — and only lay them out as cells. They never re-sort or re-filter.
//
// Two differences from the screen, both so the file can be sorted and summed:
//   • "3 / 12" in the Rank cell becomes two number columns, Rank and Peer Group Size;
//   • the Bottom Box chip beside the quadrant becomes its own Yes / blank column.
//
// Rows are built by position, never keyed by header: two parameters of a
// scorecard can carry the same name, and an object keyed by name would drop one.

import type { TableCell, TableSheet } from '../../utils/tableXlsx';

/** The fields of a View → Score row the two tables print. */
export interface ScoreSheetRow {
  employee_code: string;
  employee_name: string;
  store_name: string;
  store_code: string;
  store_address: string;
  metrics: Array<{ code: string; contribution: number }>;
  total: number;
  quadrant: string;
  date_of_joining?: string | null;
  /** The month is before the employee joined — the Total cell shows '—'. */
  preJoin?: boolean;
  is_bottom_box?: boolean;
  /** 6-month snapshot: total per month (keyed by the month) and their average. */
  six?: { totals: Record<string, number>; avg: number | null } | null;
}

/** Standing within the reporting manager's team — the "n / total" on screen. */
type RankOf<R> = (row: R) => { rank: number; total: number } | null;
/** The AON text the screen shows for a joining date ('—' when unknown). */
type AonLabel = (dateOfJoining?: string | null) => string;

/** What the screen prints in place of a missing value. In the file that is an empty cell. */
const PLACEHOLDER = '—';

const IDENTITY_HEADERS = ['Employee Name', 'Employee Code', 'Store Name', 'Store Code', 'Store Address'];
const STANDING_HEADERS = ['Rank', 'Peer Group Size', 'AON', 'Quadrant', 'Bottom Box'];
const TWO_DECIMALS = '0.00';

const identityCells = (r: ScoreSheetRow): TableCell[] => [
  r.employee_name, r.employee_code, r.store_name || null, r.store_code || null, r.store_address || null,
];

const standingCells = <R extends ScoreSheetRow>(r: R, rankOf: RankOf<R>, aonLabel: AonLabel): TableCell[] => {
  const standing = rankOf(r);
  const aon = aonLabel(r.date_of_joining);
  return [
    standing ? standing.rank : null,
    standing ? standing.total : null,
    aon === PLACEHOLDER ? null : aon,
    r.quadrant,
    r.is_bottom_box ? 'Yes' : null,
  ];
};

/** '0.00' on `count` columns starting at `first` — the score columns the screen prints with two decimals. */
const twoDecimalColumns = (first: number, count: number): Record<number, string> => {
  const formats: Record<number, string> = {};
  for (let c = first; c < first + count; c++) formats[c] = TWO_DECIMALS;
  return formats;
};

/**
 * The Monthly table: one column per parameter holding its weighted
 * contribution (the number the cell prints), then Total. A row with no entry
 * for a parameter, and the Total of a pre-join row, are empty cells.
 */
export const buildMonthlyScoreSheet = <R extends ScoreSheetRow>({
  rows, metricColumns, rankOf, aonLabel,
}: {
  rows: R[];
  metricColumns: Array<{ code: string; name: string }>;
  rankOf: RankOf<R>;
  aonLabel: AonLabel;
}): TableSheet => ({
  name: 'Monthly Score',
  headers: [...IDENTITY_HEADERS, ...metricColumns.map((m) => m.name), 'Total', ...STANDING_HEADERS],
  rows: rows.map((r) => [
    ...identityCells(r),
    ...metricColumns.map((col) => r.metrics.find((x) => x.code === col.code)?.contribution ?? null),
    r.preJoin ? null : r.total,
    ...standingCells(r, rankOf, aonLabel),
  ]),
  numberFormats: twoDecimalColumns(IDENTITY_HEADERS.length, metricColumns.length + 1),
});

/**
 * The 6-Month Avg table: one column per month of the window holding that
 * month's snapshot total, then Average. A month with no snapshot, and the
 * Average of an employee with none, are empty cells.
 */
export const buildSixMonthScoreSheet = <R extends ScoreSheetRow>({
  rows, months, monthLabel, rankOf, aonLabel,
}: {
  rows: R[];
  /** The window's months, oldest first, as the keys of `six.totals`. */
  months: string[];
  monthLabel: (month: string) => string;
  rankOf: RankOf<R>;
  aonLabel: AonLabel;
}): TableSheet => ({
  name: '6-Month Avg',
  headers: [...IDENTITY_HEADERS, ...months.map(monthLabel), 'Average', ...STANDING_HEADERS],
  rows: rows.map((r) => [
    ...identityCells(r),
    ...months.map((m) => r.six?.totals[m] ?? null),
    r.six?.avg ?? null,
    ...standingCells(r, rankOf, aonLabel),
  ]),
  numberFormats: twoDecimalColumns(IDENTITY_HEADERS.length, months.length + 1),
});
